import React, { useState, useEffect, useRef } from 'react';
import { useDownloadCart } from '../services/downloadCart';
import { api } from '../services/api';

function DownloadCart() {
  const { items, removeItem, clearCart } = useDownloadCart();
  const [downloadType, setDownloadType] = useState<'calls' | 'calls-and-transcripts'>('calls');
  const [, setJobId] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'processing' | 'complete' | 'error'>('idle');
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [jobInfo, setJobInfo] = useState<any>(null);
  const [formats, setFormats] = useState<{ id: string; name: string; pattern: string }[]>([]);
  const [selectedFormat, setSelectedFormat] = useState('');
  const [groupByDate, setGroupByDate] = useState(false);
  const [optionsChanged, setOptionsChanged] = useState(false);
  // Set on unmount so the polling loop stops and no setState fires after
  // the user navigates away.
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    return () => { cancelledRef.current = true; };
  }, []);

  useEffect(() => {
    api.listExportFormats().then((data: any) => setFormats(data.formats || [])).catch(() => {});
  }, []);

  const handleDownload = async () => {
    if (items.length === 0) return;
    setStatus('processing');
    setError(null);
    setOptionsChanged(false);
    try {
      const recordingIds = items.map(i => i.recordingId);
      const pattern = selectedFormat ? formats.find(f => f.id === selectedFormat)?.pattern : undefined;
      const result = await api.startBulkDownload(recordingIds, downloadType, pattern, groupByDate);
      setJobId(result.jobId);
      // Poll for completion
      pollForCompletion(result.jobId);
    } catch (err: any) {
      setStatus('error');
      setError(err.message || 'Failed to start download');
    }
  };

  const pollForCompletion = async (id: string) => {
    const maxAttempts = 60; // 5 minutes at 5-second intervals
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(resolve => setTimeout(resolve, 5000));
      if (cancelledRef.current) return; // navigated away — stop polling
      try {
        const result = await api.checkBulkDownload(id);
        if (cancelledRef.current) return;
        if (result.status === 'complete') {
          setStatus('complete');
          setDownloadUrl(result.downloadUrl);
          setJobInfo(result);
          return;
        }
        if (result.status === 'error') {
          setStatus('error');
          setError(result.error || 'Download job failed');
          return;
        }
      } catch {
        // Keep polling on transient errors
      }
    }
    if (cancelledRef.current) return;
    setStatus('error');
    setError('Download timed out. Please try again.');
  };

  const hasTranscripts = items.some(i => i.hasTranscript);

  return (
    <div>
      <div className="page-header">
        <h1>Download Cart</h1>
        <p>Select recordings to download as a ZIP file (max 20)</p>
      </div>

      {items.length === 0 ? (
        <div className="setup-card" style={{ textAlign: 'center', padding: 40, color: '#888' }}>
          <p style={{ fontSize: 16, marginBottom: 8 }}>Your download cart is empty</p>
          <p style={{ fontSize: 13 }}>Browse recordings and click the cart icon to add them here.</p>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <span style={{ fontSize: 14, color: '#666' }}>{items.length} of 20 slots used</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={clearCart}
                style={{ padding: '6px 14px', background: '#fff', color: '#e74c3c', border: '1px solid #e74c3c', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                Clear Cart
              </button>
            </div>
          </div>

          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Filename</th>
                <th>System</th>
                <th>Agent</th>
                <th>Phone</th>
                <th>Duration</th>
                <th>Transcript</th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.recordingId}>
                  <td>{item.recordDate}</td>
                  <td style={{ fontSize: 12, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.originalFilename}>{item.originalFilename || '-'}</td>
                  <td><span className={`system-tag ${item.systemId === '3cx-legacy' ? 'legacy' : item.systemId}`}>{item.systemId}</span></td>
                  <td>{item.agentId || '-'}</td>
                  <td>{item.callerId || '-'}</td>
                  <td>{item.duration || '-'}</td>
                  <td>{item.hasTranscript ? <span style={{ color: '#27ae60', fontWeight: 600 }}>Yes</span> : <span style={{ color: '#999' }}>No</span>}</td>
                  <td>
                    <button onClick={() => removeItem(item.recordingId)}
                      style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: 16, padding: '2px 8px' }}
                      title="Remove from cart">
                      &times;
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="setup-card" style={{ marginTop: 16 }}>
            <h3 style={{ marginBottom: 12 }}>Download Options</h3>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="radio" name="downloadType" value="calls" checked={downloadType === 'calls'}
                  onChange={() => { setDownloadType('calls'); setOptionsChanged(true); }} />
                <span style={{ fontSize: 14 }}>Calls only</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: hasTranscripts ? 'pointer' : 'not-allowed', opacity: hasTranscripts ? 1 : 0.5 }}>
                <input type="radio" name="downloadType" value="calls-and-transcripts"
                  checked={downloadType === 'calls-and-transcripts'}
                  onChange={() => { setDownloadType('calls-and-transcripts'); setOptionsChanged(true); }}
                  disabled={!hasTranscripts} />
                <span style={{ fontSize: 14 }}>Calls + Transcripts</span>
              </label>
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
              <label style={{ fontSize: 14, color: '#333' }}>Filename format:</label>
              <select value={selectedFormat} onChange={e => { setSelectedFormat(e.target.value); setOptionsChanged(true); }}
                style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, minWidth: 200 }}>
                <option value="">Original filename</option>
                {formats.map(f => (
                  <option key={f.id} value={f.id}>{f.name} — {f.pattern}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                <input type="checkbox" checked={groupByDate} onChange={e => { setGroupByDate(e.target.checked); setOptionsChanged(true); }} />
                Group files into date folders (M_D_YYYY)
              </label>
            </div>

            {status === 'idle' && (
              <button onClick={handleDownload}
                style={{ padding: '10px 24px', background: '#00bcd4', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                Download {items.length} Recording{items.length !== 1 ? 's' : ''} as ZIP
              </button>
            )}

            {status === 'processing' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 20, height: 20, border: '3px solid #ddd', borderTopColor: '#1a1a2e', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                <span style={{ fontSize: 14, color: '#666' }}>Preparing your download... This may take a minute.</span>
              </div>
            )}

            {status === 'complete' && downloadUrl && (
              <div style={{ padding: 16, background: optionsChanged ? '#fff3e0' : '#e8f5e9', borderRadius: 8 }}>
                {optionsChanged ? (
                  <p style={{ color: '#e65100', fontWeight: 600, marginBottom: 8 }}>
                    Options changed — rebuild the ZIP to apply.
                  </p>
                ) : (
                  <p style={{ color: '#2e7d32', fontWeight: 600, marginBottom: 8 }}>
                    Download ready! {jobInfo?.fileCount} file{jobInfo?.fileCount !== 1 ? 's' : ''}{jobInfo?.totalSize ? ` (${(jobInfo.totalSize / 1048576).toFixed(1)} MB)` : ''}
                  </p>
                )}
                {jobInfo?.skippedFiles?.length > 0 && (
                  <p style={{ color: '#e65100', fontSize: 13, marginBottom: 8 }}>
                    {jobInfo.skippedFiles.length} file(s) skipped (in Glacier archive — request restore first)
                  </p>
                )}
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  {optionsChanged ? (
                    <button onClick={() => { setStatus('idle'); setDownloadUrl(null); setJobInfo(null); handleDownload(); }}
                      style={{ padding: '10px 24px', background: '#e65100', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                      Rebuild ZIP
                    </button>
                  ) : (
                    <a href={downloadUrl} download
                      style={{ display: 'inline-block', padding: '10px 24px', background: '#27ae60', color: '#fff', borderRadius: 6, fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>
                      Download ZIP
                    </a>
                  )}
                  <button onClick={() => { clearCart(); setStatus('idle'); setDownloadUrl(null); setJobInfo(null); setOptionsChanged(false); }}
                    style={{ padding: '10px 24px', background: '#fff', color: '#333', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, cursor: 'pointer' }}>
                    Clear Cart
                  </button>
                </div>
                <p style={{ fontSize: 11, color: '#888', marginTop: 8 }}>Link expires in 1 hour</p>
              </div>
            )}

            {status === 'error' && (
              <div style={{ padding: 16, background: '#ffebee', borderRadius: 8 }}>
                <p style={{ color: '#c62828', fontWeight: 600, marginBottom: 8 }}>Download failed</p>
                <p style={{ color: '#c62828', fontSize: 13 }}>{error}</p>
                <button onClick={() => { setStatus('idle'); setError(null); }}
                  style={{ marginTop: 8, padding: '6px 16px', background: '#fff', color: '#c62828', border: '1px solid #c62828', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                  Try Again
                </button>
              </div>
            )}
          </div>
        </>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default DownloadCart;
